import type { Sex } from "@/components/company-census/types";
import { loadCompanyCensusEnv } from "@/env";
import { createApiClient } from "@cakewalk/frontend-api";

/**
 * Authenticated client for the company-census "Connect QuickBooks Payroll" step
 * (platform-API routes at `/api/quote-census/quickbooks`).
 *
 *   authorize → mints the Intuit OAuth URL for the caller's draft
 *   result    → the persisted pull, mapped to census-facing shapes
 *
 * The OAuth callback itself is a full-page redirect handled server-side + the
 * `/oauth/quickbooks/callback` popup page — not called from here.
 */

const QB_PATH = "/api/quote-census/quickbooks";

function baseUrl(): string {
  return loadCompanyCensusEnv().VITE_API_BASE_URL.replace(/\/$/, "");
}

function client() {
  return createApiClient({ baseUrl: baseUrl(), requireAuth: true });
}

export interface CensusQuickBooksAuthorizeResponse {
  authorizeUrl: string;
}

/** A census member as returned by the QuickBooks pull (names may be blank). */
export interface CensusQuickBooksMemberWire {
  /**
   * The QuickBooks employee id. Carried so a second pull can be merged onto the
   * roster the employer has already been editing instead of replacing it.
   */
  externalId: string | null;
  firstName: string;
  lastName: string;
  sex: Sex | null;
  zip: string | null;
  dateOfBirth: string | null;
  dependentMethod: "manual" | "none";
  enrolling: boolean;
  dependents: never[];
}

export interface CensusQuickBooksBusinessWire {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  /** The company EIN as `XX-XXXXXXX`, from CompanyInfo's `EmployerId`, when QuickBooks has it. */
  ein: string | null;
}

export interface CensusQuickBooksResult {
  connected: boolean;
  realmId: string | null;
  payrollStatus: string | null;
  companyName: string | null;
  business: CensusQuickBooksBusinessWire | null;
  members: CensusQuickBooksMemberWire[];
  employeeCount: number;
  hasCompanyInfo: boolean;
  /** Plain-language notes about census fields QuickBooks left blank. */
  fieldWarnings: string[];
  /**
   * Former employees QuickBooks returned that we left out of the roster —
   * deactivated, or terminated (which QuickBooks records as a `ReleasedDate`
   * while leaving the employee `Active`). Shown to the employer so a roster that
   * is smaller than their QuickBooks headcount reads as correct, not as a
   * partial import.
   */
  excludedFormerCount: number;
}

/** Start the Intuit OAuth for the caller's census draft. */
export function authorizeCensusQuickBooks(): Promise<CensusQuickBooksAuthorizeResponse> {
  return client().post<CensusQuickBooksAuthorizeResponse>(`${QB_PATH}/authorize`, {});
}

/** Read the persisted QuickBooks pull for the caller's census draft. */
export function getCensusQuickBooksResult(): Promise<CensusQuickBooksResult> {
  return client().get<CensusQuickBooksResult>(`${QB_PATH}/result`);
}
